import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Typography,
    TextField,
    Button,
    Skeleton,
    TablePagination,
    MenuItem,
    Select,
    InputAdornment,
    FormControl,
    InputLabel,
    Paper,
    Alert,
} from "@mui/material";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { ProcessResultCard } from "./components/ProcessResultCard";
import type { ProcessSearchRequest, ProcessSearchResponse } from "./types";
import { customInstanceBpm } from "../../../api/axios-client";
import { useAuth } from "../../../auth";
import { ActiveFilters } from "../../../components/Search/ActiveFilters";
import { FacetSidebar } from "../../../components/Search/FacetSidebar";

const searchSchema = z.object({
    query: z.string().optional(),
    page: z.number().catch(0),
    size: z.number().catch(20),
    sortBy: z.string().catch("_score"),
    sortDirection: z.string().catch("desc"),
    filters: z.string().optional(),
});

const FACET_FIELDS = [
    "processState",
    "processCategory",
    "processDefinitionName.keyword",
    "currentAssignee",
    "processStartDate",
];

const FACET_LABEL_MAP: Record<string, string> = {
    processState: "processesV2.facets.processState",
    processCategory: "processesV2.facets.processCategory",
    "processDefinitionName.keyword": "processesV2.facets.processDefinitionName",
    currentAssignee: "processesV2.facets.currentAssignee",
    processStartDate: "processesV2.facets.processStartDate",
};

const VALUE_TRANSLATION_PREFIXES: Record<string, string> = {
    processStartDate: "processesV2.dateRanges",
    processState: "processesV2.states",
};

function parseFilters(filtersStr?: string): Record<string, string[]> {
    if (!filtersStr) return {};
    try {
        return JSON.parse(filtersStr);
    } catch {
        return {};
    }
}

function serializeFilters(filters: Record<string, string[]>): string | undefined {
    const cleaned: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(filters)) {
        if (v.length > 0) cleaned[k] = v;
    }
    if (Object.keys(cleaned).length === 0) return undefined;
    return JSON.stringify(cleaned);
}

type SearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/__auth/processes-v2/")({
    component: ProcessesV2Component,
    staticData: {
        title: "routes.processesV2",
    },
    validateSearch: (search) => searchSchema.parse(search),
});

function buildSearchRequest(
    params: SearchParams,
    activeFilters: Record<string, string[]>
): ProcessSearchRequest {
    const finalQuery = params.query?.trim() || undefined;

    const effectiveSortBy =
        (!finalQuery && params.sortBy === "_score") ? "processStartDate" : params.sortBy;

    const req: ProcessSearchRequest = {
        query: finalQuery,
        page: params.page,
        size: params.size,
        sortBy: effectiveSortBy,
        sortDirection: params.sortDirection,
        facetFields: FACET_FIELDS,
    };

    // Enviados como lista: o backend combina os valores do mesmo campo como OU
    if (activeFilters.processState?.length) req.processState = activeFilters.processState;
    if (activeFilters.processCategory?.length) req.processCategory = activeFilters.processCategory;
    if (activeFilters["processDefinitionName.keyword"]?.length)
        req.processDefinitionName = activeFilters["processDefinitionName.keyword"];
    if (activeFilters.currentAssignee?.length) req.currentAssignee = activeFilters.currentAssignee;
    if (activeFilters.processStartDate?.length)
        req.processStartDateRange = activeFilters.processStartDate;

    return req;
}

function ProcessesV2Component() {
    const { t } = useTranslation();
    const { currentTenant } = useAuth();
    const search = Route.useSearch();
    const navigate = useNavigate({ from: Route.fullPath });

    const [localQuery, setLocalQuery] = useState(search.query || "");

    const activeFilters = useMemo(() => parseFilters(search.filters), [search.filters]);

    const searchRequest = useMemo(
        () => buildSearchRequest(search, activeFilters),
        [search, activeFilters]
    );

    const { data, isLoading, isError } = useQuery({
        queryKey: ["processes-v2-search", currentTenant, searchRequest],
        queryFn: async (): Promise<ProcessSearchResponse> => {
            return customInstanceBpm<ProcessSearchResponse>(
                `/v1/${currentTenant || "default-domain"}/search/processes`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(searchRequest),
                }
            );
        },
        enabled: !!currentTenant,
        placeholderData: keepPreviousData,
    });

    const results = (data?.results || []).filter((r) => !r.score || r.score > 0);
    const totalHits = data?.totalHits || 0;
    const facets = data?.facets;

    const handleSearch = useCallback(() => {
        navigate({
            search: (prev) => ({
                ...prev,
                query: localQuery.trim() || undefined,
                page: 0,
            }),
        });
    }, [navigate, localQuery]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") handleSearch();
    };

    const handleFilterChange = useCallback(
        (facetKey: string, value: string, checked: boolean) => {
            const current = { ...activeFilters };
            const values = current[facetKey] || [];
            current[facetKey] = checked
                ? [...values, value]
                : values.filter((v) => v !== value);

            navigate({
                search: (prev) => ({ ...prev, filters: serializeFilters(current), page: 0 }),
            });
        },
        [navigate, activeFilters]
    );

    const handleRemoveFilter = useCallback(
        (facetKey: string, value: string) => handleFilterChange(facetKey, value, false),
        [handleFilterChange]
    );

    const handleClearAllFilters = useCallback(() => {
        navigate({ search: (prev) => ({ ...prev, filters: undefined, page: 0 }) });
    }, [navigate]);

    const handlePageChange = (_: unknown, newPage: number) => {
        navigate({
            search: (prev) => ({ ...prev, page: newPage }),
            replace: true,
        });
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        navigate({
            search: (prev) => ({
                ...prev,
                size: parseInt(e.target.value, 10),
                page: 0,
            }),
            replace: true,
        });
    };

    const handleSortChange = (sortBy: string) => {
        navigate({
            search: (prev) => ({ ...prev, sortBy, sortDirection: "desc" }),
        });
    };

    return (
        <Box sx={{ p: 2, pb: 10 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                }}
            >
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t("processesV2.searchPlaceholder")}
                    value={localQuery}
                    onChange={(e) => setLocalQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            bgcolor: "background.default",
                        },
                    }}
                />
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    disableElevation
                    size="large"
                    sx={{
                        whiteSpace: "nowrap",
                        px: 4,
                        textTransform: "uppercase",
                        fontWeight: 600,
                        letterSpacing: 0.5,
                    }}
                >
                    {t("common.search")}
                </Button>
            </Paper>

            {isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {t("processesV2.searchError")}
                </Alert>
            )}

            {Object.values(activeFilters).some((v) => v.length > 0) && (
                <Box sx={{ mb: 2 }}>
                    <ActiveFilters
                        filters={activeFilters}
                        onRemove={handleRemoveFilter}
                        onClearAll={handleClearAllFilters}
                        labelMap={FACET_LABEL_MAP}
                        clearLabelKey="processesV2.clearFilters"
                    />
                </Box>
            )}

            <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>
                {facets && Object.keys(facets).length > 0 && (
                    <Box
                        sx={{
                            width: 260,
                            flexShrink: 0,
                            display: { xs: "none", md: "block" },
                            position: "sticky",
                            top: 80,
                        }}
                    >
                        <FacetSidebar
                            facets={facets}
                            activeFilters={activeFilters}
                            onFilterChange={handleFilterChange}
                            labelMap={FACET_LABEL_MAP}
                            valueTranslationPrefixes={VALUE_TRANSLATION_PREFIXES}
                        />
                    </Box>
                )}

                <Box sx={{ flex: 1, minWidth: 0 }}>

            {!isLoading && !isError && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        pb: 1.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        <Box component="span" sx={{ fontWeight: 700, color: "text.primary", fontSize: "1rem" }}>
                            {totalHits.toLocaleString()}
                        </Box>
                        {" "}{t("processesV2.results")}
                    </Typography>

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>{t("processesV2.sortBy")}</InputLabel>
                        <Select
                            value={search.sortBy || "_score"}
                            label={t("processesV2.sortBy")}
                            onChange={(e) => handleSortChange(e.target.value)}
                        >
                            <MenuItem value="_score">{t("processesV2.relevance")}</MenuItem>
                            <MenuItem value="processStartDate">{t("processesV2.startDate")}</MenuItem>
                            <MenuItem value="processEndDate">{t("processesV2.endDate")}</MenuItem>
                            <MenuItem value="codeSortable">{t("processesV2.nup")}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}

            {isLoading ? (
                <Box>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton
                            key={i}
                            variant="rectangular"
                            height={100}
                            sx={{ mb: 1.5, borderRadius: 2 }}
                        />
                    ))}
                </Box>
            ) : isError ? null : results.length === 0 ? (
                <Box sx={{ p: 6, textAlign: "center" }}>
                    <Typography variant="h6" color="text.secondary">
                        {t("processesV2.noResults")}
                    </Typography>
                </Box>
            ) : (
                results.map((result, index) => (
                    <ProcessResultCard key={result.id || index} result={result} />
                ))
            )}

            {!isError && totalHits > 0 && (
                <TablePagination
                    component="div"
                    count={totalHits}
                    page={search.page || 0}
                    onPageChange={handlePageChange}
                    rowsPerPage={search.size || 20}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("common.itemsPerPage")}
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    sx={{
                        mt: 2,
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                />
            )}
                </Box>
            </Box>
        </Box>
    );
}